<template>
  <div
    class="admin-shell"
    :class="{
      'admin-shell--compact': compact,
      'admin-shell--menu-open': menuOpen
    }"
  >
    <div v-if="compact" class="admin-mobile-bar">
      <el-button class="admin-mobile-bar__menu" text @click="menuOpen = true">
        <el-icon><Menu /></el-icon>
        <span>菜单</span>
      </el-button>
      <span class="admin-mobile-bar__title">管理后台</span>
      <router-link to="/" class="admin-mobile-bar__home" @click="menuOpen = false">返回站点</router-link>
    </div>

    <div v-if="compact && menuOpen" class="admin-backdrop" @click="menuOpen = false" />

    <el-container class="admin-container">
      <el-aside class="aside" :class="{ 'aside--drawer': compact }" width="240px">
        <div class="brand" @click="navigate('/admin')">
          <div class="brand__title">管理后台</div>
          <div class="brand__sub">CoolPeople Admin</div>
        </div>

        <el-menu
          :default-active="active"
          :default-openeds="openedMenus"
          class="menu"
          router
          @select="onMenuSelect"
        >
          <el-menu-item index="/admin">
            <el-icon><Odometer /></el-icon>
            <span>概览</span>
          </el-menu-item>

          <el-sub-menu index="group-user">
            <template #title>
              <el-icon><User /></el-icon>
              <span>用户与审核</span>
            </template>
            <el-menu-item index="/admin/users">用户管理</el-menu-item>
            <el-menu-item index="/admin/user-audit">用户资料审核</el-menu-item>
            <el-menu-item index="/admin/idcard-audit">身份证审核</el-menu-item>
            <el-menu-item index="/admin/images">图片审核</el-menu-item>
            <el-menu-item index="/admin/roles">角色与权限</el-menu-item>
            <el-menu-item index="/admin/referral-coupons">推荐券管理</el-menu-item>
            <el-menu-item index="/admin/referral-bindings">推荐绑定 / 分润</el-menu-item>
            <el-menu-item index="/admin/referral-user-overview">拉新用户全景</el-menu-item>
            <el-menu-item index="/admin/purchase-refund-appeals">购买申诉退款</el-menu-item>
            <el-menu-item index="/admin/partner-applications">合伙人申请</el-menu-item>
            <el-menu-item index="/admin/withdraw-applications">提现审核</el-menu-item>
            <el-menu-item index="/admin/user-ip-logs">登录 / IP 日志</el-menu-item>
          </el-sub-menu>

          <el-sub-menu index="group-home">
            <template #title>
              <el-icon><HomeFilled /></el-icon>
              <span>首页运营</span>
            </template>
            <el-menu-item index="/admin/home-carousel">主页轮播图</el-menu-item>
            <el-menu-item index="/admin/home-news">最新动态</el-menu-item>
          </el-sub-menu>

          <el-sub-menu index="group-content">
            <template #title>
              <el-icon><Document /></el-icon>
              <span>内容与测评</span>
            </template>
            <el-menu-item index="/admin/blogs">编辑推荐管理</el-menu-item>
            <el-menu-item index="/admin/exames">测评管理</el-menu-item>
            <el-menu-item index="/admin/exam-badges">测评徽章配置</el-menu-item>
            <el-menu-item index="/admin/exam-results">测评完成记录</el-menu-item>
          </el-sub-menu>

          <el-sub-menu index="group-knowledge">
            <template #title>
              <el-icon><Reading /></el-icon>
              <span>知识库</span>
            </template>
            <el-menu-item index="/admin/knowledge/courses">课程管理</el-menu-item>
            <el-menu-item index="/admin/knowledge/points">知识点管理</el-menu-item>
            <el-menu-item index="/admin/knowledge/resources">资源管理</el-menu-item>
          </el-sub-menu>

          <el-sub-menu v-if="isSuperAdmin" index="group-system">
            <template #title>
              <el-icon><Setting /></el-icon>
              <span>系统</span>
            </template>
            <el-menu-item index="/admin/database-backup">数据库备份</el-menu-item>
          </el-sub-menu>
        </el-menu>
      </el-aside>

      <el-main class="main">
        <router-view />
      </el-main>
    </el-container>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  User,
  Document,
  Odometer,
  Reading,
  HomeFilled,
  Setting,
  Menu
} from '@element-plus/icons-vue'
import { isSuperAdmin as checkSuperAdmin } from '@/utils/sysRole'
import { isCompactViewport } from '@/utils/browser'

const GROUP_BY_PATH = [
  { prefix: '/admin/users', group: 'group-user' },
  { prefix: '/admin/user-audit', group: 'group-user' },
  { prefix: '/admin/idcard-audit', group: 'group-user' },
  { prefix: '/admin/images', group: 'group-user' },
  { prefix: '/admin/roles', group: 'group-user' },
  { prefix: '/admin/invite-codes', group: 'group-user' },
  { prefix: '/admin/invite-tree', group: 'group-user' },
  { prefix: '/admin/user-ip-logs', group: 'group-user' },
  { prefix: '/admin/purchase-refund-appeals', group: 'group-user' },
  { prefix: '/admin/partner-applications', group: 'group-user' },
  { prefix: '/admin/withdraw-applications', group: 'group-user' },
  { prefix: '/admin/home-carousel', group: 'group-home' },
  { prefix: '/admin/home-news', group: 'group-home' },
  { prefix: '/admin/blogs', group: 'group-content' },
  { prefix: '/admin/exames', group: 'group-content' },
  { prefix: '/admin/exam-badges', group: 'group-content' },
  { prefix: '/admin/exam-results', group: 'group-content' },
  { prefix: '/admin/exam/create', group: 'group-content' },
  { prefix: '/admin/exam/edit', group: 'group-content' },
  { prefix: '/admin/exam/', group: 'group-content' },
  { prefix: '/admin/knowledge/courses', group: 'group-knowledge' },
  { prefix: '/admin/knowledge/points', group: 'group-knowledge' },
  { prefix: '/admin/knowledge/resources', group: 'group-knowledge' },
  { prefix: '/admin/database-backup', group: 'group-system' }
]

export default {
  name: 'AdminLayout',
  components: {
    User,
    Document,
    Odometer,
    Reading,
    HomeFilled,
    Setting,
    Menu
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const compact = ref(isCompactViewport())
    const menuOpen = ref(false)

    const onResize = () => {
      compact.value = isCompactViewport()
      if (!compact.value) {
        menuOpen.value = false
      }
    }

    onMounted(() => {
      window.addEventListener('resize', onResize)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', onResize)
    })

    const isSuperAdmin = computed(() => {
      try {
        const user = JSON.parse(localStorage.getItem('user_info') || '{}')
        return checkSuperAdmin(user.sysRole)
      } catch {
        return false
      }
    })

    const active = computed(() => {
      const path = route.path
      if (path.startsWith('/admin/knowledge/courses')) return '/admin/knowledge/courses'
      if (path.startsWith('/admin/knowledge/points')) return '/admin/knowledge/points'
      if (path.startsWith('/admin/knowledge/resources')) return '/admin/knowledge/resources'
      if (path.startsWith('/admin/exam/')) return '/admin/exam'
      if (path.startsWith('/admin')) return path
      return '/admin'
    })

    const openedMenus = computed(() => {
      const path = route.path
      for (const { prefix, group } of GROUP_BY_PATH) {
        if (path === prefix || path.startsWith(prefix + '/')) {
          return [group]
        }
      }
      return []
    })

    const navigate = (p) => {
      router.push(p)
      menuOpen.value = false
    }

    const onMenuSelect = () => {
      menuOpen.value = false
    }

    return {
      active,
      openedMenus,
      navigate,
      onMenuSelect,
      isSuperAdmin,
      compact,
      menuOpen
    }
  }
}
</script>

<style scoped>
.admin-shell {
  min-height: 100vh;
  background: #f5f7fa;
}
.admin-container {
  min-height: 100vh;
}
.aside {
  background: #0f1b33;
  color: #eaf0ff;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  padding-right: 10px;
  box-sizing: border-box;
}
.brand {
  padding: 16px 14px 14px 14px;
  cursor: pointer;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.brand__title {
  font-weight: 800;
  letter-spacing: 0.2px;
}
.brand__sub {
  margin-top: 4px;
  font-size: 12px;
  opacity: 0.75;
}
.menu {
  border-right: none;
  padding-right: 6px;
  background: transparent;
  --el-menu-bg-color: transparent;
  --el-menu-hover-bg-color: rgba(255, 255, 255, 0.06);
  --el-menu-active-color: #eaf0ff;
  --el-menu-text-color: rgba(234, 240, 255, 0.86);
}
:deep(.el-menu-item),
:deep(.el-sub-menu__title) {
  color: rgba(234, 240, 255, 0.86);
}
:deep(.el-menu-item.is-active) {
  background: rgba(78, 161, 255, 0.18);
  color: #eaf0ff;
}
:deep(.el-menu-item:hover),
:deep(.el-sub-menu__title:hover) {
  background: rgba(255, 255, 255, 0.06);
}
:deep(.el-sub-menu.is-active > .el-sub-menu__title) {
  color: #eaf0ff;
}
:deep(.el-sub-menu .el-menu) {
  background: rgba(0, 0, 0, 0.15);
}
:deep(.el-sub-menu .el-menu-item) {
  min-width: auto;
  padding-left: 48px !important;
}
.main {
  padding: 20px 32px 20px 24px;
  overflow-x: auto;
  box-sizing: border-box;
}

.admin-mobile-bar {
  display: none;
}

.admin-backdrop {
  display: none;
}

@media (max-width: 767px) {
  .admin-mobile-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: #0f1b33;
    color: #eaf0ff;
    position: sticky;
    top: 0;
    z-index: 1900;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  .admin-mobile-bar__menu {
    color: #eaf0ff !important;
    padding: 4px 8px !important;
  }

  .admin-mobile-bar__menu .el-icon {
    margin-right: 4px;
  }

  .admin-mobile-bar__title {
    flex: 1;
    font-weight: 700;
    font-size: 15px;
  }

  .admin-mobile-bar__home {
    font-size: 13px;
    color: rgba(234, 240, 255, 0.85);
    text-decoration: none;
    white-space: nowrap;
  }

  .admin-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 2000;
    background: rgba(0, 0, 0, 0.45);
  }

  .aside--drawer {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 2100;
    height: 100vh;
    overflow-y: auto;
    transform: translateX(-100%);
    transition: transform 0.25s ease;
  }

  .admin-shell--menu-open .aside--drawer {
    transform: translateX(0);
  }

  .main {
    padding: 12px !important;
    width: 100%;
  }

  .admin-page {
    padding: 8px 0 !important;
  }
}
</style>
